import { useEffect, useMemo, useState } from 'react';
import defaultDates from '../../../utils/defaultDates';
import useHierarchyData from '../../../hooks/useHierarchyData';
import toMillis from '../../../helpers/dataHelpers/matrixDataHelpers/toMillis';
import MultipleMomentBarChart from '../../Visualisations/moment/MultipleMomentBarChart';
import useChartColors from '../../../hooks/useChartColors';
import { useFirestoreGeneralOrderBy } from '../../../firebase/useFirestore';
import EventFieldInfoPopover from './EventFieldInfoPopover';
import ChartDataTable from '../ChartDataTable';
import { multipleChoiceTable } from '../../../helpers/dataHelpers/chartDataTable';
import fetchOptionsById from '../../data/fetchOptionsById';
import bucketEventChoices from '../../../helpers/dataHelpers/bucketEventChoices';

// AI-CONTEXT: See docs/features/api-data-fetching.md

/**
 * @param {object} props
 * @param {{ ID: string }} props.field
 * @param {string} [props.color] - Single-color override applied to all bars (legacy / fallback).
 * @param {Array<string>} [props.colorPalette]
 * @param {Object<string, string>} [props.colorOverrides] - per-option color map keyed by option ID. '__other__' targets the synthetic Other bar.
 * @param {string} [props.startDate]
 * @param {string} [props.endDate]
 * @param {string} [props.activityId]
 */
const EventMultipleData = ({ field, color, colorPalette, colorOverrides, startDate, endDate, activityId }) => {
  const { defaultStartDate: dsRaw, defaultEndDate: deRaw } = useMemo(() => defaultDates(), []);
  const dsMs = useMemo(() => toMillis(dsRaw) ?? 0, [dsRaw]);
  const deMs = useMemo(() => toMillis(deRaw) ?? 8640000000000000, [deRaw]);
  const startMs = useMemo(() => toMillis(startDate) ?? dsMs, [startDate, dsMs]);
  const endMs = useMemo(() => toMillis(endDate) ?? deMs, [endDate, deMs]);

  const { palette, overrides, inLiveReport } = useChartColors({ colorPalette, colorOverrides });

  // The field's own options, ordered by Position: only for a stable palette
  // position per option. Names and 'Anders' come from optionsById below.
  const options = useFirestoreGeneralOrderBy(
    'MultipleQuestionOptions',
    'Field',
    field?.ID || '',
    'Position',
    'asc'
  );

  // Option ID → palette position. The 'other' option is the synthetic
  // '__other__' id so it matches the picker swatch.
  const positionById = useMemo(() => {
    const map = new Map();
    (Array.isArray(options) ? options : []).forEach((opt, idx) => {
      if (!opt?.ID) return;
      map.set(opt.Option === 'other' ? '__other__' : opt.ID, idx);
    });
    return map;
  }, [options]);

  const { records, hierarchy: projectHierarchy, loading } = useHierarchyData({
    syncType: 'Event',
    id: field.ID,
    startDate: startMs,
    endDate: endMs,
  });

  // AI-CONTEXT: See docs/pitfalls.md#security-free-text-hides-in-a-choice-answer
  // The options these answers point to — also those of linked projects, which
  // the field's own option list doesn't have. A registration carries no
  // OptionType, so only the option tells an 'Anders, namelijk' answer (whose
  // Value is the respondent's free text) from a normal one.
  const [optionsState, setOptionsState] = useState(/** @type {{ status: 'loading'|'ready'|'error', byId: Object<string, object> }} */ ({ status: 'loading', byId: {} }));
  useEffect(() => {
    if (loading) return undefined;
    let cancelled = false;
    setOptionsState({ status: 'loading', byId: {} });
    fetchOptionsById(records)
      .then((byId) => { if (!cancelled) setOptionsState({ status: 'ready', byId }); })
      .catch((err) => {
        console.error('EventMultipleData: keuze-opties niet op te halen', err);
        if (!cancelled) setOptionsState({ status: 'error', byId: {} });
      });
    return () => { cancelled = true; };
  }, [records, loading]);

  const { chartData, totalResponses } = useMemo(() => {
    // Until the options are known nothing is drawn: without them a free-text
    // answer can't be told from an option name.
    if (loading || optionsState.status !== 'ready' || !records.length) return { chartData: [], totalResponses: 0 };

    const buckets = bucketEventChoices(records, optionsState.byId);

    const responses = buckets.map((bucket) => {
      const info = {
        optionId: bucket.optionId,
        position: positionById.has(bucket.optionId) ? positionById.get(bucket.optionId) : positionById.size,
      };
      const name = bucket.name;
      // Volgorde: gekozen kleur → de ene kleur die de aanroeper meegaf →
      // het palet, doorlopen op de vaste Position van de optie. Buiten een live
      // rapportage vallen de eerste twee weg: `color` is daar de kaartkleur die
      // we juist niet meer volgen.
      const overrideColor = overrides?.[info.optionId];
      const resolvedColor = overrideColor
        || (inLiveReport ? color : null)
        || palette[info.position % palette.length];
      return {
        id: info.optionId,
        name,
        count: bucket.count,
        color: resolvedColor,
      };
    });

    return {
      chartData: [{ moment: 'Totaal', responses }],
      totalResponses: buckets.reduce((sum, b) => sum + b.count, 0),
    };
  }, [records, loading, optionsState, positionById, overrides, inLiveReport, color, palette]);

  return (
    <>
      {(field?.Title || activityId) && (
        <div className='home-kpi-question-title-container'>
          {field?.Title && <p className='event-field-title'>{field.Title}</p>}
          <EventFieldInfoPopover field={field} activityId={activityId} totalResponses={totalResponses} projectHierarchy={projectHierarchy} />
        </div>
      )}
      {optionsState.status === 'error' ? (
        <p className='graph-data-error'>De gegevens konden niet worden geladen.</p>
      ) : (
      <MultipleMomentBarChart
        data={chartData}
        projectAverages={{}}
        totalResponses={totalResponses}
        projectHierarchy={projectHierarchy}
        color={color}
        source="event"
      />
      )}
      <ChartDataTable table={multipleChoiceTable(chartData)} />
    </>
  );
};

export default EventMultipleData;
